import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';  
import api from '../services/api';
import '../styles/escola.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';
import { useNavigate } from 'react-router-dom';

const UsuarioManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [perfis, setPerfis] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    const dadosParaEnviar = { ...dadosAtualizacao };
    delete dadosParaEnviar.salvar;
    delete dadosParaEnviar.excluir;

    const permissoesObj = {
      ...(dadosParaEnviar.permissoes || {}),
      modo_consulta: Boolean(dadosParaEnviar.modo_consulta),
      edita_tabela_tipo: Boolean(dadosParaEnviar.edita_tabela_tipo),
      cria_questionario: Boolean(dadosParaEnviar.cria_questionario),
      acessa_bdados: Boolean(dadosParaEnviar.acessa_bdados),
      acessa_sistema: Boolean(dadosParaEnviar.acessa_sistema),
      acessa_resultados: Boolean(dadosParaEnviar.acessa_resultados),
      cria_acoes: Boolean(dadosParaEnviar.cria_acoes),
    };
    
    dadosParaEnviar.permissoes = permissoesObj;

    delete dadosParaEnviar.modo_consulta;
    delete dadosParaEnviar.edita_tabela_tipo;
    delete dadosParaEnviar.editar_tabelastipo;
    delete dadosParaEnviar.cria_questionario;
    delete dadosParaEnviar.pode_criar_questionarios;
    delete dadosParaEnviar.acessa_bdados;
    delete dadosParaEnviar.acessa_sistema;
    delete dadosParaEnviar.acessa_resultados;
    delete dadosParaEnviar.cria_acoes;

    try {
      const response = await api.put(`/usuario/${id}`, dadosParaEnviar);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Usuário atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      let mensagemErro = 'Erro ao atualizar registro';
      if (error.response?.data?.message) {
        mensagemErro = `Erro: ${error.response.data.message}`;
      } else if (error.message) {
        mensagemErro = `Erro: ${error.message}`;
      }
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    try {
      await api.delete(`/usuario/${id}`);
      setDados(currentData => currentData.filter(item => item.id_usuario !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      let mensagemErro = 'Erro ao excluir registro';
       if (error.response?.data?.message) {
        mensagemErro = `Erro: ${error.response.data.message}`;
      } else if (error.message) {
        mensagemErro = `Erro: ${error.message}`;
      }
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const cpfFormatter = useCallback((cell) => {
    const cpf = cell.getValue();
    if (!cpf) return '';
    // Remove non-digit characters
    const cleanCpf = String(cpf).replace(/\D/g, '');
    // Apply CPF mask XXX.XXX.XXX-XX
    return cleanCpf.replace(/(\d{3})(\d)/, '$1.$2')
                   .replace(/(\d{3})(\d)/, '$1.$2')
                   .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }, []);

  const fetchPerfis = useCallback(async () => {
    try {
      const response = await api.get('/perfis');
      console.log('Perfis carregados:', response.data.data);
      setPerfis(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      mostrarMensagem('error', `Erro ao carregar perfis: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    // Criar mapeamento de id_perfil para nome_perfil
    const perfisNomes = perfis.reduce((acc, item) => ({ 
      ...acc, 
      [item.id_perfil]: item.nome_perfil 
    }), {});
    
    console.log('Mapeamento de perfis:', perfisNomes);

    const colunasBase = [
        { title: 'Nome completo', field: 'nome_completo', minWidth: 200, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...', frozen: true },
        { title: 'Email', field: 'email', minWidth: 200, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...' },
        { title: 'CPF', field: 'cpf', width: 150, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...', formatter: cpfFormatter },
        { title: 'Telefone', field: 'telefone', width: 150, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...' },
        { title: 'Cargo', field: 'cargo', width: 150, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...' },
        { 
          title: 'Perfil', 
          field: 'id_perfil', 
          width: 150, 
          editor: 'list', 
          editorParams: { values: perfisNomes },
          sorter: 'string', 
          headerFilter: 'input', 
          headerFilterPlaceholder: 'Filtrar...',
          formatter: (cell) => perfisNomes[cell.getValue()] || cell.getValue(),
          headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
            if (!headerValue) return true;
            const nomePerfil = perfisNomes[rowValue] || rowValue || '';
            return nomePerfil.toLowerCase().includes(headerValue.toLowerCase());
          }
        },
        { title: 'Status', field: 'status_aprovacao', width: 120, editor: 'list', editorParams: { values: ["APROVADO", "PENDENTE", "REJEITADO"] }, sorter: 'string', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "APROVADO": "Aprovado", "PENDENTE": "Pendente", "REJEITADO": "Rejeitado" } } },
        { title: 'Ativo', field: 'ativo', width: 100, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Modo consulta', field: 'modo_consulta', width: 170, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Acessa o sistema', field: 'acessa_sistema', width: 190, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Cria questionário', field: 'cria_questionario', width: 200, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Acessa questionário (resultados)', field: 'acessa_resultados', width: 320, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Acessa BDados', field: 'acessa_bdados', width: 180, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Edita tabela tipo', field: 'edita_tabela_tipo', width: 180, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'Cria ações', field: 'cria_acoes', width: 150, hozAlign: 'center', formatter: 'tickCross', editor: true, sorter: 'boolean', headerFilter: 'list', headerFilterParams: { values: { "": "Todos", "true": "Sim", "false": "Não" } } },
        { title: 'UF', field: 'uf', width: 80, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...' },
        { title: 'INEP', field: 'codigo_inep', width: 100, editor: 'input', sorter: 'string', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...' },
        { 
          title: 'IBGE7', 
          field: 'codigo_ibge7', 
          width: 100, 
          editor: 'input', 
          sorter: 'string', 
          headerFilter: 'input', 
          headerFilterPlaceholder: 'Filtrar...',
          formatter: (cell) => {
            const value = cell.getValue();
            if (!value) return '';
            // Para municípios (código com 7 dígitos), mostrar apenas os 2 primeiros (código do estado)
            if (value.toString().length === 7) {
              return value.toString().substring(0, 2);
            }
            return value;
          }
        },
        { title: 'ID Regional', field: 'id_regional', width: 150, editor: 'number', sorter: 'number', headerFilter: 'input', headerFilterPlaceholder: 'Filtrar...' },
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_usuario, data);
        },
        headerSort: false,
        frozen: true
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o usuário "${data.nome_completo}"?`)) {
            excluirRegistro(data.id_usuario);
          }
        },
        headerSort: false,
        frozen: true
      },
      ...colunasBase
    ];
  }, [atualizarRegistro, excluirRegistro, cpfFormatter, perfis]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitDataStretch',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      resizableColumns: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_usuario'
    });
  }, [dados, getColunas]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/usuario'); 
      const rawData = response.data.data || [];
      const formattedData = rawData.map(u => ({
        ...u,
        modo_consulta: u.permissoes?.modo_consulta || false,
        edita_tabela_tipo: u.permissoes?.edita_tabela_tipo || false,
        cria_questionario: u.permissoes?.cria_questionario || false,
        acessa_bdados: u.permissoes?.acessa_bdados || false,
        acessa_sistema: u.permissoes?.acessa_sistema || false,
        acessa_resultados: u.permissoes?.acessa_resultados || false,
        cria_acoes: u.permissoes?.cria_acoes || false,
      }));
      setDados(formattedData);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${formattedData.length} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);
  
  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/usuario'); 
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchPerfis();
  }, [verificarAPI, fetchPerfis]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      console.log('Inicializando tabulator...');
      inicializarTabulator();
    }
  }, [dados, getColunas, apiStatus, inicializarTabulator]);
  
  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'usuarios.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de usuários</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para gerenciar usuários do sistema</p>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>
            {mensagem.texto}
          </div>
        </div>
      )}
      
      <div className="controls">
          <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
            {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
          </button>
          <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default UsuarioManager;